//@flow
import {
    outlet as outletApi,
    bulkOutletFetch as bulkOutletFetchApi,
    disableOutlet as disableOutletApi,
    bulkDisableOutlet as bulkDisableOutletApi,
    outletLocationBulkGeocode,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import I18nHelper from 'common/helpers/I18nHelper';

import { GeocodingMetadata } from 'common/models/internal/GeocodingMetadata';

import type { MasterOutlet } from 'common/models/internal/MasterOutlet';

import type { Dispatch } from 'redux';
import { ENTITY_NAME_ID } from 'common/constants/entity/outletMaster';

type Params = {
    clientId: string,
    outletId?: string,
    overwrite?: boolean
};

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

class OutletActions extends PaginatedEntityActions<MasterOutlet, Params> {
    superAddEntity = this.addEntity;
    entityNameId = ENTITY_NAME_ID;

    types = {
        ...this.types,
        OUTLET_BULK_GEOCODE_SUCCESS: 'OUTLET_BULK_GEOCODE_SUCCESS'
    };

    bulkGeocodeSuccess = (params, response, locations) => {
        return dispatch =>
            dispatch({
                type: this.types.OUTLET_BULK_GEOCODE_SUCCESS,
                params,
                locations,
                response
            });
    };

    addEntity = (
        params: { clientId: string, outletId: string },
        entity: MasterOutlet,
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (outlet: MasterOutlet) => {
                        const toast = {
                            message: nsTranslate('addEntitySuccessMessage', {
                                entityNameId: this.entityNameId
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(outlet);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(this.superAddEntity(params, entity, options));
            });
        };
    };

    superPatchEntity = this.patchEntity;

    patchEntity = (
        params: { clientId: string, locationId: string },
        oldEntity: MasterOutlet,
        newEntity: MasterOutlet,
        options?: { dontInjectError?: boolean, suppressToasts?: boolean } = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const { dontInjectError, suppressToasts } = options;
                const patchOptions = {
                    onSuccess: (outlet: MasterOutlet) => {
                        if (!suppressToasts) {
                            const toast = {
                                message: nsTranslate('savedChangesMessage'),
                                type: ToastTypes.Success
                            };
                            dispatch(showToast(toast, params));
                        }
                        resolve(outlet);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError,
                    getExtraPatchOptions: ({ jsonDiff, entity }) => {
                        if (
                            jsonDiff.some(diff =>
                                diff.path.startsWith('/locationInfo/location')
                            )
                        ) {
                            return [
                                {
                                    op: 'test',
                                    path: '/locationInfo/location/version',
                                    value: entity.locationInfo.location.version
                                }
                            ];
                        }
                    }
                };
                dispatch(
                    this.superPatchEntity(
                        params,
                        oldEntity,
                        newEntity,
                        patchOptions
                    )
                );
            });
        };
    };

    superDisableEntity = this.disableEntity;

    disableEntity = (
        params: { clientId: string, locationId: string },
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (location: MasterOutet) => {
                        const toast = {
                            message: nsTranslate('deleteEntitySuccessMessage', {
                                entityNameId: this.entityNameId
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(location);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                dispatch(this.superDisableEntity(params, options));
            });
        };
    };

    toggleLocationVerification = (
        params: { clientId: string, locationId: string },
        location: MasterLocation,
        options?: { dontInjectError?: boolean, suppressToasts?: boolean } = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const { dontInjectError, suppressToasts } = options;
                const patchOptions = {
                    onSuccess: (location: MasterLocation) => {
                        if (!suppressToasts) {
                            const toast = {
                                message: nsTranslate(
                                    'updateEntitySuccessMessage',
                                    { entityNameId: this.entityNameId }
                                ),
                                type: ToastTypes.Success
                            };
                            dispatch(showToast(toast, params));
                        }
                        resolve(location);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError
                };
                let updatedGeocodingMetadata = GeocodingMetadata.fromJS({
                    verified: true
                });
                const { geocodingMetadata } = location;
                if (geocodingMetadata) {
                    updatedGeocodingMetadata = geocodingMetadata.setVerified(
                        !geocodingMetadata.verified
                    );
                }
                const updatedLocation = location.setGeocodingMetadata(
                    updatedGeocodingMetadata
                );
                dispatch(
                    this.superPatchEntity(
                        params,
                        location,
                        updatedLocation,
                        patchOptions
                    )
                );
            });
        };
    };

    bulkGeocode = (
        params: { clientId: string },
        locations: Array<GeocodeEditLocation> = [],
        options: Object = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            const locationIds = locations.map(location => location.id);
            const body = { locationIds };
            const { onSuccess, onFailure } = options;
            this.geocodeApi
                .post(params, body)
                .then(() => {
                    onSuccess && onSuccess(locationIds);
                })
                .catch(error => {
                    const toast = {
                        message: error.message,
                        type: ToastTypes.Error
                    };
                    dispatch(showToast(toast, params));
                    onFailure && onFailure();
                });
        };
    };
}

export default new OutletActions(
    'OUTLET',
    outletApi,
    bulkOutletFetchApi,
    disableOutletApi,
    bulkDisableOutletApi,
    validateEntityIdsApi,
    outletLocationBulkGeocode
);
